/*
  NextDoseChangeCard — a small heads-up banner on the Rx detail page that
  tells the user (and carers) when the next tapering transition is coming up.

  Rendering rules:
    - Rx without tapering_schedule: renders nothing.
    - Today is inside band i and band i+1 exists:
        "Steps down from 12 → 8 tablets in 3 days (Fri Apr 25)"
    - Today is inside the last band (no band i+1):
        "Final band — course ends in 5 days (Tue Apr 29)"
    - Today is before day 1 (scheduled but not started):
        "Starts in 2 days — begins at 12 tablets"
    - Today is past the last band:
        "Course complete — confirm with GP before continuing"
*/
import { useMemo } from "react";
import { TrendingDown, TrendingUp, ArrowRight, CheckCircle2, CalendarClock } from "lucide-react";

function parseDayAmount(s) {
  // "12 tablets" → 12 ; "12.5ml" → 12.5 ; "one tablet" → NaN
  const m = String(s || "").match(/-?\d+(?:\.\d+)?/);
  return m ? parseFloat(m[0]) : NaN;
}

function formatIn(days) {
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `in ${days} day${days === 1 ? "" : "s"}`;
}

function formatDateShort(d) {
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export default function NextDoseChangeCard({ rx }) {
  const info = useMemo(() => {
    if (!rx?.tapering_schedule?.length || !rx.start_datetime) return null;
    const bands = [...rx.tapering_schedule].sort((a, b) => a.day_from - b.day_from);
    // Today offset (1-indexed) from start, using calendar days
    const start = new Date(rx.start_datetime);
    const startMid = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const now = new Date();
    const nowMid = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const msPerDay = 86400000;
    const dayOffset = Math.floor((nowMid - startMid) / msPerDay) + 1;
    const lastEnd = bands[bands.length - 1].day_to;

    // Course not yet started
    if (dayOffset < 1) {
      const daysUntilStart = 1 - dayOffset;
      const firstBand = bands[0];
      return {
        kind: "pre_start",
        daysUntil: daysUntilStart,
        when: new Date(startMid.getTime() + (1 - 1) * msPerDay),
        nextAmount: firstBand.dose_amount,
      };
    }

    // Course already finished
    if (dayOffset > lastEnd) {
      return { kind: "complete" };
    }

    // Find current + next band
    let currentIdx = bands.findIndex((b) => dayOffset >= b.day_from && dayOffset <= b.day_to);
    if (currentIdx === -1) return null;
    const current = bands[currentIdx];
    const next = bands[currentIdx + 1];

    if (!next) {
      // Inside the last band — show countdown to course end
      const daysUntilEnd = current.day_to - dayOffset + 1;
      const endDate = new Date(startMid.getTime() + (current.day_to - 1) * msPerDay);
      return {
        kind: "final_band",
        daysUntil: daysUntilEnd - 1, // days until the LAST dose day
        when: endDate,
        currentAmount: current.dose_amount,
      };
    }

    // Normal transition between bands
    const daysUntilChange = current.day_to - dayOffset + 1;
    const changeDate = new Date(startMid.getTime() + current.day_to * msPerDay);
    const cur = parseDayAmount(current.dose_amount);
    const nxt = parseDayAmount(next.dose_amount);
    let direction = "change";
    if (!isNaN(cur) && !isNaN(nxt)) {
      direction = nxt < cur ? "down" : nxt > cur ? "up" : "change";
    }
    return {
      kind: "transition",
      daysUntil: daysUntilChange,
      when: changeDate,
      currentAmount: current.dose_amount,
      nextAmount: next.dose_amount,
      direction,
    };
  }, [rx]);

  if (!info) return null;

  if (info.kind === "complete") {
    return (
      <section
        data-testid="next-dose-change-complete"
        className="mb-4 rounded-xl border-l-4 p-3.5 flex items-start gap-3 card-tactile"
        style={{ borderLeftColor: "var(--primary)" }}
      >
        <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "var(--primary-ink)" }} />
        <div className="min-w-0">
          <div className="label-caps mb-0.5">Course complete</div>
          <p className="text-sm" style={{ color: "var(--text-main)" }}>
            All tapering bands finished — confirm with your GP before continuing or refilling.
          </p>
        </div>
      </section>
    );
  }

  if (info.kind === "pre_start") {
    return (
      <section
        data-testid="next-dose-change-pre-start"
        className="mb-4 rounded-xl border-l-4 p-3.5 flex items-start gap-3 card-tactile"
        style={{ borderLeftColor: "var(--primary)" }}
      >
        <CalendarClock className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "var(--primary-ink)" }} />
        <div className="min-w-0">
          <div className="label-caps mb-0.5">Starts {formatIn(info.daysUntil).toLowerCase()}</div>
          <p className="text-sm" style={{ color: "var(--text-main)" }}>
            Begins at <b>{info.nextAmount}</b> on {formatDateShort(info.when)}.
          </p>
        </div>
      </section>
    );
  }

  if (info.kind === "final_band") {
    return (
      <section
        data-testid="next-dose-change-final"
        className="mb-4 rounded-xl border-l-4 p-3.5 flex items-start gap-3 card-tactile"
        style={{ borderLeftColor: "var(--primary)" }}
      >
        <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "var(--primary-ink)" }} />
        <div className="min-w-0">
          <div className="label-caps mb-0.5">Final band</div>
          <p className="text-sm" style={{ color: "var(--text-main)" }}>
            Currently <b>{info.currentAmount}</b> — course ends {info.daysUntil === 0 ? "today" : formatIn(info.daysUntil).toLowerCase()}
            {" "}({formatDateShort(info.when)}).
          </p>
        </div>
      </section>
    );
  }

  // transition
  const Icon = info.direction === "down" ? TrendingDown : info.direction === "up" ? TrendingUp : ArrowRight;
  const iconColor = info.direction === "down" ? "var(--primary-ink)" : "var(--warning)";
  const borderColor = info.direction === "down" ? "var(--primary)" : "var(--warning)";
  const verb = info.direction === "down" ? "Steps down" : info.direction === "up" ? "Steps up" : "Changes";
  const isImminent = info.daysUntil <= 1;

  return (
    <section
      data-testid="next-dose-change-card"
      className="mb-4 rounded-xl border-l-4 p-3.5 flex items-start gap-3 card-tactile"
      style={{ borderLeftColor: borderColor }}
    >
      <Icon className="h-5 w-5 shrink-0 mt-0.5" style={{ color: iconColor }} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <span className="label-caps">Next dose change</span>
          {isImminent && (
            <span
              data-testid="next-dose-change-imminent"
              className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full"
              style={{
                background: "var(--warning-bg)",
                color: "var(--warning)",
              }}
            >
              Heads up
            </span>
          )}
        </div>
        <p className="text-sm leading-relaxed" style={{ color: "var(--text-main)" }}>
          {verb} from <b data-testid="next-dose-change-current">{info.currentAmount}</b>
          {" "}<ArrowRight className="h-3.5 w-3.5 inline align-text-bottom" style={{ color: "var(--text-muted)" }} />{" "}
          <b data-testid="next-dose-change-next">{info.nextAmount}</b>
          {" "}<span data-testid="next-dose-change-when">{formatIn(info.daysUntil)}</span>
          {" "}<span style={{ color: "var(--text-muted)" }}>({formatDateShort(info.when)})</span>.
        </p>
      </div>
    </section>
  );
}
